import { useState, useEffect, useRef } from "react";
import { applySeo } from "@/lib/seo";
import { AgencyLogo } from "@/components/AgencyLogo";
import { CheckoutButton } from "@/components/CheckoutButton";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { BAROMETER_PRICES, PAKET_PRICES, KOMPLETT_EINZELSUMME, KOMPLETT_ERSPARNIS, KOMPLETT_PAKET_MIT_VIDEOCALL, VIDEOCALL_1H_AUFPREIS, formatPrice } from "@shared/prices";

const LOGO_URL = "/manus-storage/logo_2d439bd1.jpg";
const PORTRAIT_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310519663476281654/fDRgangGWFphFKzWtNAEav/larissa-portrait_abbef783.jpg";

const C = {
  accent: "#6d0000",
  accentDark: "#4f0f0f",
  accentLight: "#6d000012",
  teal: "#115751",
  bg: "#ffffff",
  bgGray: "#f2f2f2",
  text: "#000000",
  muted: "#6b7280",
  border: "#e5e7eb",
};

const BUNDLE_PRICE = PAKET_PRICES.komplett;
const SINGLE_TOTAL = KOMPLETT_EINZELSUMME;
const SAVINGS = KOMPLETT_ERSPARNIS;

function AnimatedScore({ target, duration = 1800 }: { target: number; duration?: number }) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    let start = 0;
    const step = target / (duration / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else setCount(Math.floor(start));
    }, 16);
    return () => clearInterval(timer);
  }, [target, duration]);
  return <span ref={ref}>{count}</span>;
}

const ALL_BAROMETER = [
  {
    number: "01",
    icon: "/manus-storage/11_094992a3.svg",
    name: "Sensorik-Barometer™",
    price: BAROMETER_PRICES.sensorik,
    color: "#6d0000",
    path: "/sensorik",
    desc: "Emotionale Wirkung, Farben, Sprache, Bilder, CTAs – wie deine Website auf das Unterbewusstsein wirkt",
    criteria: "100+ Kriterien · 6 Dimensionen",
  },
  {
    number: "02",
    icon: "/manus-storage/1_55991aa3.svg",
    name: "Lokal-Barometer™",
    price: BAROMETER_PRICES.lokal,
    color: "#115751",
    path: "/lokal",
    desc: "Google Maps, lokale SEO, NAP-Konsistenz, Branchenverzeichnisse – lokal gefunden werden",
    criteria: "150+ Kriterien · 8 Dimensionen",
  },
  {
    number: "03",
    icon: "/manus-storage/7_8bcad2d2.svg",
    name: "GEO-Barometer™",
    price: BAROMETER_PRICES.geo,
    color: "#1a3a5c",
    path: "/geo-barometer",
    desc: "KI-Sichtbarkeit in ChatGPT, Perplexity, Google AI Overviews – die Suche der Zukunft",
    criteria: "130+ Kriterien · 8 Dimensionen",
  },
  {
    number: "04",
    icon: "/manus-storage/23_1ab7147e.svg",
    name: "SEO-Barometer™",
    price: BAROMETER_PRICES.seo,
    color: "#0a5c36",
    path: "/seo-barometer",
    desc: "SEO-Grundlagen-Check: On-Page SEO, technische SEO, Core Web Vitals (Google PageSpeed), E-E-A-T – kein Backlink-Check",
    criteria: "100+ Kriterien · 7 Dimensionen",
  },
  {
    number: "05",
    icon: "/manus-storage/33_d51b7819.svg",
    name: "Conversion-Barometer™",
    price: BAROMETER_PRICES.conversion,
    color: "#b45309",
    path: "/conversion-barometer",
    desc: "Cialdini-Prinzipien, Preispsychologie, Micro-Conversions, Mobile UX – mehr Anfragen und Käufe",
    criteria: "160 Kriterien · 8 Dimensionen",
  },
  {
    number: "06",
    icon: "/manus-storage/16_9c3f7718.svg",
    name: "Accessibility-Barometer™",
    price: BAROMETER_PRICES.accessibility,
    color: "#3730a3",
    path: "/accessibility-barometer",
    desc: "WCAG 2.2, ARIA, Tastaturnavigation, European Accessibility Act – rechtssicher und inklusiv",
    criteria: "80+ Kriterien · 6 Dimensionen",
  },
  {
    number: "07",
    icon: "/manus-storage/29_7f3bb394.svg",
    name: "Datenschutz-Barometer™",
    price: BAROMETER_PRICES.datenschutz,
    color: "#0f766e",
    path: "/datenschutz-barometer",
    desc: "DSGVO, Cookie-Consent, Impressum, SSL, externe Dienste – Bußgelder vermeiden",
    criteria: "70+ Kriterien · 6 Dimensionen",
  },
  {
    number: "08",
    icon: "/manus-storage/41_3269a7aa.svg",
    name: "Wettbewerbs-Barometer™",
    price: BAROMETER_PRICES.wettbewerb,
    color: "#6d0000",
    path: "/wettbewerb-barometer",
    desc: "Vergleich mit bis zu 3 Wettbewerbern – automatisch oder manuell – in 8 Dimensionen",
    criteria: "8 Dimensionen · 4-Website-Vergleich",
  },
  {
    number: "09",
    icon: "/manus-storage/21_453003f0.svg",
    name: "E-Commerce-Barometer™",
    price: BAROMETER_PRICES.ecommerce,
    color: "#6d0000",
    path: "/ecommerce-barometer",
    desc: "Shops und Dienstleister: Checkout, Vertrauen, Preispsychologie, Mobile Commerce",
    criteria: "120+ Kriterien · 8 Dimensionen",
  },
];

const KOMPLETT_PRODUCT_IDS = [
  "sensorik_analyse", "lokal_analyse", "geo_analyse", "seo_analyse",
  "conversion_analyse", "accessibility_analyse", "datenschutz_analyse",
  "ecommerce_analyse", "wettbewerb_analyse",
] as const;

const PRODUCT_URL_MAP: Record<string, string> = {
  sensorik_analyse: "/sensorik",
  lokal_analyse: "/lokal",
  geo_analyse: "/geo-barometer",
  seo_analyse: "/seo-barometer",
  conversion_analyse: "/conversion-barometer",
  accessibility_analyse: "/accessibility-barometer",
  datenschutz_analyse: "/datenschutz-barometer",
  ecommerce_analyse: "/ecommerce-barometer",
  wettbewerb_analyse: "/wettbewerb-barometer",
};

export default function KomplettPaket() {
  useEffect(() => applySeo({
    title: "Komplett Website-RealitätsCheck™ – Alle 9 Barometer in einem | WERBEAGENTUR Larissa Girenko",
    description: "SEO, GEO, Sensorik, Conversion, Accessibility, Datenschutz, Lokal, E-Commerce & Wettbewerb in einer Vollanalyse. Komplett-Paket mit Videocall.",
    path: "/komplett-paket",
  }), []);
  const [visible, setVisible] = useState(false);
  const [codeInput, setCodeInput] = useState("");
  const [codeError, setCodeError] = useState<string | null>(null);
  const [redeemedTokens, setRedeemedTokens] = useState<Array<{ token: string; productId: string }> | null>(null);
  const redeemWithCode = trpc.payment.redeemWithCode.useMutation();

  useEffect(() => { const t = setTimeout(() => setVisible(true), 100); return () => clearTimeout(t); }, []);

  const handleCodeRedeem = async () => {
    setCodeError(null);
    try {
      const result = await redeemWithCode.mutateAsync({
        code: codeInput,
        productIds: [...KOMPLETT_PRODUCT_IDS],
      });
      if (result.success && result.tokens) {
        setRedeemedTokens(result.tokens);
        toast.success("Code eingelöst! Alle 9 Analysen sind freigeschaltet.");
      } else {
        setCodeError((result as any).reason ?? "Code ungültig");
      }
    } catch (err: any) {
      setCodeError(err.message ?? "Fehler beim Einlösen");
    }
  };

  // Erfolgsseite nach Code-Einlösung
  if (redeemedTokens) {
    return (
      <div className="min-h-screen bg-white" style={{ fontFamily: "'DM Sans', sans-serif" }}>
        <header className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3">
            <AgencyLogo withLogo size="sm" />
          </a>
        </header>
        <section className="max-w-2xl mx-auto px-6 py-16 text-center">
          <div className="text-5xl mb-6">✓</div>
          <h1 className="font-black uppercase mb-4" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(2rem, 6vw, 3.5rem)", color: C.accent }}>
            ALLE 9 ANALYSEN FREIGESCHALTET!
          </h1>
          <p className="text-gray-500 mb-10">Dein Komplett Website-RealitätsCheck™ ist bereit. Klicke auf ein Barometer, um die Analyse zu starten.</p>
          <div className="space-y-3 text-left">
            {redeemedTokens.map(({ token, productId }) => {
              const barometer = ALL_BAROMETER.find(b => b.path === PRODUCT_URL_MAP[productId]);
              const url = PRODUCT_URL_MAP[productId];
              if (!url) return null;
              return (
                <a
                  key={token}
                  href={`${url}?token=${token}`}
                  className="flex items-center justify-between p-4 border-2 no-underline transition-all"
                  style={{ borderColor: barometer?.color ?? C.accent, backgroundColor: `${barometer?.color ?? C.accent}0d` }}
                >
                  <div className="flex items-center gap-3">
                    <img src={barometer?.icon ?? "/manus-storage/32_04885248.svg"} alt="" className="w-7 h-7 object-contain flex-shrink-0" style={{ opacity: 0.85 }} />
                    <div>
                      <p className="font-black uppercase text-sm" style={{ fontFamily: "'Bebas Neue', sans-serif", color: barometer?.color ?? C.accent }}>{barometer?.name ?? productId}</p>
                    </div>
                  </div>
                  <span className="text-xs font-bold uppercase tracking-widest px-3 py-1 text-white" style={{ backgroundColor: barometer?.color ?? C.accent }}>Analyse starten →</span>
                </a>
              );
            })}
          </div>
          <button onClick={() => { setRedeemedTokens(null); setCodeInput(""); }}
            className="mt-8 text-xs font-bold uppercase tracking-widest text-gray-400 hover:text-gray-700 transition-colors">
            Zurück zur Übersicht
          </button>
        </section>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col" style={{ fontFamily: "'DM Sans', sans-serif" }}>

      {/* Header */}
      <header className="border-b border-gray-200 px-6 py-4 flex items-center justify-between bg-white sticky top-0 z-50">
        <a href="/" className="flex items-center gap-3">
          <img src={LOGO_URL} alt="WERBEAGENTUR Larissa Girenko" className="w-10 h-10 object-contain" />
          <div>
            <span className="text-black font-bold text-sm tracking-widest uppercase" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
              WERBEAGENTUR Larissa Girenko
            </span>
            <p className="text-gray-400 text-xs hidden md:block">Marketing Barometer Suite</p>
          </div>
        </a>
        <a href="/" className="text-xs font-bold tracking-widest uppercase px-3 py-1.5 border transition-colors"
          style={{ color: "#6b7280", borderColor: "#e5e7eb" }}>← Übersicht</a>
      </header>

      {/* Hero */}
      <section className="w-full bg-black overflow-hidden">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-stretch" style={{ minHeight: "520px" }}>
          <div className="flex-1 px-8 md:px-16 py-14 flex flex-col justify-center">
            <p className="text-xs font-bold tracking-widest uppercase mb-4" style={{ color: C.accent, fontFamily: "'DM Sans', sans-serif" }}>
              Das vollständige Marketing-Audit
            </p>
            <h1 className="font-black uppercase text-white mb-4 leading-none" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(2.5rem, 7vw, 5rem)" }}>
              KOMPLETT-PAKET™
            </h1>
            <p className="text-gray-300 text-lg mb-6 max-w-xl" style={{ fontFamily: "'DM Sans', sans-serif" }}>
              Alle 9 Barometer. Ein vollständiges Bild deiner Website – sensorisch, lokal, KI-Sichtbarkeit, SEO, Conversion, Accessibility, Datenschutz, Wettbewerb und E-Commerce.
            </p>
            <div className="flex flex-wrap items-baseline gap-3 mb-2">
              <span className="font-black text-white" style={{ fontSize: "3.5rem", fontFamily: "'Bebas Neue', sans-serif", lineHeight: "1" }}>{BUNDLE_PRICE} €</span>
              <span className="text-gray-500 line-through text-xl">{SINGLE_TOTAL} €</span>
            </div>
            <div className="flex flex-wrap gap-3 mb-3">
              <span className="text-sm font-bold px-3 py-1" style={{ backgroundColor: "#16a34a", color: "white", fontFamily: "'DM Sans', sans-serif" }}>
                −{SAVINGS} € gespart (28%)
              </span>
              <span className="text-sm font-bold px-3 py-1 border" style={{ borderColor: C.accent, color: C.accent, fontFamily: "'DM Sans', sans-serif" }}>
                = {(BUNDLE_PRICE / 9).toFixed(0)} € pro Barometer
              </span>
            </div>
            <p className="text-xs text-yellow-500 font-bold mb-6" style={{ fontFamily: "'DM Sans', sans-serif" }}>
              ⏳ Aktionspreis – nur für begrenzte Zeit
            </p>
            <CheckoutButton
              productId="komplett_paket"
              label={`Jetzt alle 9 Analysen starten – ${BUNDLE_PRICE} €`}
              accentColor={C.teal}
              returnPath="/komplett-paket"
            />
            {/* Zugangscode-Feld */}
            <div className="mt-4">
              <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-2">Oder Zugangscode einlösen:</p>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={codeInput}
                  onChange={e => { setCodeInput(e.target.value.toUpperCase()); setCodeError(null); }}
                  onKeyDown={e => e.key === "Enter" && handleCodeRedeem()}
                  placeholder="Z.B. LARISSA-TEST-2026"
                  className="flex-1 border border-gray-600 bg-black text-white px-3 py-2 text-sm font-mono uppercase focus:outline-none focus:border-gray-400"
                  style={{ letterSpacing: "0.05em" }}
                />
                <button
                  onClick={handleCodeRedeem}
                  disabled={redeemWithCode.isPending || !codeInput.trim()}
                  className="px-4 py-2 text-xs font-bold uppercase tracking-widest text-white transition-opacity disabled:opacity-50"
                  style={{ backgroundColor: C.teal }}
                >
                  {redeemWithCode.isPending ? "…" : "Einlösen"}
                </button>
              </div>
              {codeError && <p className="text-xs text-red-400 mt-2">{codeError}</p>}
            </div>
          </div>
          <div className="hidden md:block w-80 lg:w-96 relative overflow-hidden flex-shrink-0">
            <img src={PORTRAIT_IMG} alt="Larissa Girenko" className="w-full h-full object-cover object-top" style={{ minHeight: "520px" }} />
            <div className="absolute inset-0" style={{ background: "linear-gradient(to right, #000 0%, transparent 40%)" }} />
          </div>
        </div>
      </section>

      {/* Savings-Banner */}
      <section style={{ backgroundColor: C.accent }}>
        <div className="max-w-5xl mx-auto px-6 py-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-6">
            <div className="text-center">
              <div className="font-black text-white text-3xl" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>9</div>
              <div className="text-xs text-red-200 uppercase tracking-widest">Analysen</div>
            </div>
            <div className="w-px h-10 bg-red-400" />
            <div className="text-center">
              <div className="font-black text-white text-3xl" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
                {visible ? <AnimatedScore target={800} /> : 0}+
              </div>
              <div className="text-xs text-red-200 uppercase tracking-widest">Kriterien</div>
            </div>
            <div className="w-px h-10 bg-red-400" />
            <div className="text-center">
              <div className="font-black text-white text-3xl" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
                {SAVINGS} €
              </div>
              <div className="text-xs text-red-200 uppercase tracking-widest">Ersparnis</div>
            </div>
            <div className="w-px h-10 bg-red-400" />
            <div className="text-center">
              <div className="font-black text-white text-3xl" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>28%</div>
              <div className="text-xs text-red-200 uppercase tracking-widest">Rabatt</div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl mx-auto">
            <div className="border border-gray-600 p-4 flex flex-col text-left">
              <p className="text-xs font-bold tracking-widest uppercase mb-1 text-gray-400" style={{ fontFamily: "'DM Sans', sans-serif" }}>Option 1</p>
              <h4 className="font-black uppercase text-base mb-1 text-white" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>Vollständige Analyse</h4>
              <p className="text-gray-400 text-xs mb-3 flex-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>9 Analysen, PDF-Report, Lieferung in 72h.</p>
              <div className="text-2xl font-black text-white mb-3" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{BUNDLE_PRICE} €</div>
              <CheckoutButton productId="komplett_paket" label="Jetzt buchen" accentColor={C.teal} returnPath="/komplett-paket" fullWidth />
            </div>
            <div className="border-2 p-4 flex flex-col text-left relative" style={{ borderColor: C.teal }}>
              <div className="absolute -top-3 left-3 px-2 py-0.5 text-white text-xs font-bold uppercase tracking-widest" style={{ backgroundColor: C.teal, fontFamily: "'DM Sans', sans-serif" }}>Empfohlen</div>
              <p className="text-xs font-bold tracking-widest uppercase mb-1" style={{ color: C.teal, fontFamily: "'DM Sans', sans-serif" }}>Option 2</p>
              <h4 className="font-black uppercase text-base mb-1 text-white" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>Analyse + VideoCall</h4>
              <p className="text-gray-400 text-xs mb-3 flex-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>9 Analysen + 1h persönliche Ergebnis-Interpretation mit Larissa per VideoCall.</p>
              <div className="text-2xl font-black mb-1" style={{ fontFamily: "'Bebas Neue', sans-serif", color: C.teal }}>{formatPrice(KOMPLETT_PAKET_MIT_VIDEOCALL)}</div>
              <p className="text-xs text-gray-500 mb-3" style={{ fontFamily: "'DM Sans', sans-serif" }}>inkl. 1h-VideoCall (+{VIDEOCALL_1H_AUFPREIS} €)</p>
              <CheckoutButton productId="komplett_paket_mit_videocall" label="Analyse + VideoCall" accentColor={C.teal} returnPath="/komplett-paket" fullWidth />
            </div>
          </div>
        </div>
      </section>

      {/* Alle 9 Barometer */}
      <section className="max-w-5xl mx-auto px-6 py-16 w-full">
        <p className="text-xs font-bold tracking-widest uppercase mb-2 text-center" style={{ color: C.accent }}>Was du bekommst</p>
        <h2 className="font-black uppercase text-center mb-12" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(1.8rem, 4vw, 3rem)" }}>
          ALLE 9 BAROMETER IM ÜBERBLICK
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {ALL_BAROMETER.map((b) => (
            <div key={b.number} className="border border-gray-200 p-5 hover:border-gray-400 transition-colors">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <img src={b.icon} alt={b.name} className="w-7 h-7 object-contain flex-shrink-0" style={{ opacity: 0.85 }} />
                  <span className="text-xs font-bold uppercase tracking-widest" style={{ color: C.muted }}>#{b.number}</span>
                </div>
                <span className="text-xs font-bold" style={{ color: b.color }}>{b.price} €</span>
              </div>
              <h3 className="font-black uppercase mb-1" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "1.1rem", color: b.color }}>{b.name}</h3>
              <p className="text-xs text-gray-600 mb-3 leading-relaxed">{b.desc}</p>
              <p className="text-xs font-bold uppercase tracking-widest" style={{ color: C.muted }}>{b.criteria}</p>
            </div>
          ))}
        </div>

        {/* Preis-Vergleich */}
        <div className="border-2 p-8" style={{ borderColor: C.accent }}>
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <p className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: C.accent }}>Preisvergleich</p>
              <div className="flex items-baseline gap-3 mb-2">
                <span className="text-gray-400 line-through text-xl">{SINGLE_TOTAL} € Einzelkauf</span>
                <span className="font-black text-4xl" style={{ fontFamily: "'Bebas Neue', sans-serif", color: C.accent }}>{BUNDLE_PRICE} €</span>
              </div>
              <p className="text-sm text-gray-600">Du sparst <strong>{SAVINGS} €</strong> (28 % Rabatt) – einmalig, kein Abo.</p>
            </div>
            <div className="flex-shrink-0 flex flex-col gap-2">
              <CheckoutButton productId="komplett_paket" label={`Analyse – ${BUNDLE_PRICE} €`} accentColor={C.teal} returnPath="/komplett-paket" fullWidth />
              <CheckoutButton productId="komplett_paket_mit_videocall" label={`+ VideoCall – ${formatPrice(KOMPLETT_PAKET_MIT_VIDEOCALL)}`} accentColor="#6d0000" returnPath="/komplett-paket" fullWidth />
            </div>
          </div>
        </div>
      </section>

      {/* Vergleich mit anderen Bundles */}
      <section className="bg-gray-50 py-16">
        <div className="max-w-5xl mx-auto px-6">
          <p className="text-xs font-bold tracking-widest uppercase mb-2 text-center" style={{ color: C.accent }}>Paket-Vergleich</p>
          <h2 className="font-black uppercase text-center mb-10" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(1.8rem, 4vw, 2.5rem)" }}>
            WELCHES PAKET PASST ZU DIR?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Website RealitätsCheck */}
            <div className="bg-white border border-gray-200 p-6">
              <p className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: "#115751" }}>Website RealitätsCheck™</p>
              <p className="font-black text-2xl mb-3" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{formatPrice(PAKET_PRICES.website)}</p>
              <p className="text-xs text-gray-500 mb-4">SEO + Lokal + Accessibility + Datenschutz</p>
              <p className="text-xs text-gray-600 mb-4">Ideal für: KMUs, lokale Dienstleister, die technisch und rechtlich auf Vordermann wollen.</p>
              <a href="/website-realitaetscheck" className="block text-center text-xs font-bold uppercase tracking-widest py-2 border transition-colors"
                style={{ borderColor: "#115751", color: "#115751" }}>Details ansehen</a>
            </div>
            {/* Marketing RealitätsCheck */}
            <div className="bg-white border border-gray-200 p-6">
              <p className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: C.accent }}>Marketing RealitätsCheck™</p>
              <p className="font-black text-2xl mb-3" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{formatPrice(PAKET_PRICES.marketing)}</p>
              <p className="text-xs text-gray-500 mb-4">Alle 7 klassischen Barometer</p>
              <p className="text-xs text-gray-600 mb-4">Ideal für: Unternehmen, die ihr gesamtes Online-Marketing analysieren wollen.</p>
              <a href="/marketing-realitaetscheck" className="block text-center text-xs font-bold uppercase tracking-widest py-2 border transition-colors"
                style={{ borderColor: C.accent, color: C.accent }}>Details ansehen</a>
            </div>
            {/* Komplett Website-RealitätsCheck */}
            <div className="border-2 p-6" style={{ borderColor: C.accent, backgroundColor: "#6d000008" }}>
              <div className="flex items-center justify-between mb-1">
                <p className="text-xs font-bold uppercase tracking-widest" style={{ color: C.accent }}>Komplett Website-RealitätsCheck™</p>
                <span className="text-xs font-bold px-2 py-0.5 text-white" style={{ backgroundColor: C.accent }}>BEST VALUE</span>
              </div>
              <p className="font-black text-2xl mb-3" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{formatPrice(PAKET_PRICES.komplett)}</p>
              <p className="text-xs text-gray-500 mb-4">Alle 9 Barometer inkl. Wettbewerb + E-Commerce</p>
              <p className="text-xs text-gray-600 mb-4">Ideal für: Agenturen, E-Commerce, ambitionierte Unternehmer – das vollständige Bild.</p>
              <CheckoutButton
                productId="komplett_paket"
                label={`Jetzt kaufen – ${formatPrice(PAKET_PRICES.komplett)}`}
                accentColor={C.teal}
                returnPath="/komplett-paket"
                fullWidth
              />
            </div>
          </div>
        </div>
      </section>

      {/* Larissa-Sektion */}
      <section className="bg-black py-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="text-xs font-bold tracking-widest uppercase mb-4" style={{ color: C.accent }}>Deine Analystin</p>
          <h2 className="font-black uppercase text-white mb-4" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(1.8rem, 4vw, 3rem)" }}>
            LARISSA GIRENKO
          </h2>
          <p className="text-gray-300 max-w-2xl mx-auto mb-8 text-base leading-relaxed">
            Ich bin Larissa Girenko, Inhaberin der WERBEAGENTUR Larissa Girenko. Mit dem Komplett Website-RealitätsCheck™ bekommst du das vollständigste Marketing-Audit, das ich anbiete – 9 Analysen, 800+ Kriterien, ein klares Bild deiner gesamten Online-Präsenz.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <CheckoutButton productId="komplett_paket" label={`Analyse – ${BUNDLE_PRICE} €`} accentColor={C.teal} returnPath="/komplett-paket" />
            <CheckoutButton productId="komplett_paket_mit_videocall" label={`Analyse + VideoCall – ${formatPrice(KOMPLETT_PAKET_MIT_VIDEOCALL)}`} accentColor="#6d0000" returnPath="/komplett-paket" />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-200 py-8 px-6">
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="WERBEAGENTUR Larissa Girenko" className="w-8 h-8 object-contain" />
            <span className="text-xs font-bold uppercase tracking-widest text-gray-500" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
              WERBEAGENTUR Larissa Girenko
            </span>
          </div>
          <div className="flex gap-6">
            <a href="/impressum" className="text-xs text-gray-400 hover:text-gray-700 uppercase tracking-widest transition-colors">Impressum</a>
            <a href="/datenschutz" className="text-xs text-gray-400 hover:text-gray-700 uppercase tracking-widest transition-colors">Datenschutz</a>
            <a href="/" className="text-xs text-gray-400 hover:text-gray-700 uppercase tracking-widest transition-colors">Alle Barometer</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
